import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import { AuthService } from '../auth/auth.service';
import { ThemeService } from '../theme/theme.service';
import { CustomerLookupService } from '../customers/customer-lookup.service';
import { MarketplaceService } from '../marketplace/marketplace.service';
import { TranslatePipe } from '../i18n/translate.pipe';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private static readonly ADMIN_MODE_KEY = 'castor.runner.adminMode';

  loading = signal(false);
  error = signal<string | null>(null);
  showNewPasswordForm = signal(false);
  redirectingToHostedUi = signal(false);
  form: FormGroup;
  newPasswordForm: FormGroup;
  readonly themeService = inject(ThemeService);
  private hubUnsubscribe: (() => void) | null = null;
  private lastAdminMode = false;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private customerLookup: CustomerLookupService,
    private marketplace: MarketplaceService
  ) {
    this.form = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      adminMode: [false],
    });
    this.newPasswordForm = this.fb.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(8), this.cognitoPasswordPolicyValidator]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator },
    );
    this.lastAdminMode = false;
    this.form.get('adminMode')?.setValue(false);
  }

  /** Matches Castor Cognito pool policy: min 8 chars, upper, lower, number. */
  private cognitoPasswordPolicyValidator = (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v || v.length < 8) return null; // minLength handles length
    const hasLower = /[a-z]/.test(v);
    const hasUpper = /[A-Z]/.test(v);
    const hasNumber = /[0-9]/.test(v);
    return hasLower && hasUpper && hasNumber ? null : { cognitoPolicy: true };
  };

  private passwordMatchValidator(g: FormGroup): { mismatch: boolean } | null {
    const newPassword = g.get('newPassword')?.value;
    const confirm = g.get('confirmPassword')?.value;
    return newPassword && confirm && newPassword !== confirm ? { mismatch: true } : null;
  }

  ngOnInit(): void {
    this.resetLoginAdminMode();

    this.hubUnsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          this.setAdminMode(true);
          await this.cacheCustomerFromAuth();
          await this.navigateAfterSignIn(true);
          break;
        case 'signInWithRedirect_failure':
          this.redirectingToHostedUi.set(false);
          this.error.set((payload.data as { error?: { message?: string } })?.error?.message ?? 'OAuth sign-in failed');
          break;
      }
    });

    const fromInstitucional = this.route.snapshot.queryParamMap.get('from') === 'institucional';
    const hasOAuthCode = this.route.snapshot.queryParamMap.has('code');
    if (fromInstitucional && !hasOAuthCode) {
      this.redirectingToHostedUi.set(true);
      this.auth.signInWithRedirect();
    }
  }

  ngOnDestroy(): void {
    this.hubUnsubscribe?.();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  private async cacheCustomerForEmail(email: string): Promise<void> {
    const trimmed = email.trim();
    if (!trimmed) return;
    const customer = await this.customerLookup.getByEmail(trimmed);
    if (customer && customer.name) {
      const payload = {
        email: trimmed,
        customerId: customer.customerId,
        name: customer.name,
      };
      sessionStorage.setItem('castor.runner.customer', JSON.stringify(payload));
    }
  }

  private async cacheCustomerFromAuth(): Promise<void> {
    const email = await this.auth.getEmail();
    if (!email) return;
    await this.cacheCustomerForEmail(email);
  }

  private setAdminMode(isAdmin: boolean): void {
    this.lastAdminMode = isAdmin;
    sessionStorage.setItem(LoginComponent.ADMIN_MODE_KEY, isAdmin ? '1' : '0');
  }

  private resetLoginAdminMode(): void {
    this.lastAdminMode = false;
    this.form.get('adminMode')?.setValue(false, { emitEvent: false });
    sessionStorage.setItem(LoginComponent.ADMIN_MODE_KEY, '0');
  }

  private async navigateAfterSignIn(isAdmin: boolean): Promise<void> {
    if (isAdmin) {
      await this.router.navigate(['/cruds']);
      return;
    }

    try {
      let customerId: string | undefined;
      const cachedRaw = sessionStorage.getItem('castor.runner.customer');
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw) as { customerId?: string };
          customerId = cached.customerId;
        } catch {
          // ignore parse errors and fall back to no customerId filter
        }
      }

      const cruds = await this.marketplace.listAccessibleCruds(customerId);
      if (cruds.length > 0) {
        await this.router.navigate(['/run', cruds[0].crudId]);
      } else {
        await this.router.navigate(['/cruds']);
      }
    } catch {
      await this.router.navigate(['/cruds']);
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading()) return;
    this.error.set(null);
    this.loading.set(true);
    const { username, password, adminMode } = this.form.value as {
      username: string;
      password: string;
      adminMode: boolean | null;
    };
    const isAdmin = adminMode ?? false;
    this.setAdminMode(isAdmin);
    const res = await this.auth.signIn(username, password);
    this.loading.set(false);
    if (res.isSignedIn) {
      await this.cacheCustomerForEmail(username);
      await this.navigateAfterSignIn(isAdmin);
    } else if (res.requiresNewPassword) {
      this.lastAdminMode = isAdmin;
      this.showNewPasswordForm.set(true);
    } else {
      this.error.set(res.error ?? 'Could not sign in');
    }
  }

  backToLogin(): void {
    this.showNewPasswordForm.set(false);
    this.error.set(null);
    this.newPasswordForm.reset();
  }

  async submitNewPassword(): Promise<void> {
    if (this.newPasswordForm.invalid || this.loading()) return;
    this.error.set(null);
    this.loading.set(true);
    const newPassword = this.newPasswordForm.get('newPassword')?.value as string;
    const res = await this.auth.confirmSignInWithNewPassword(newPassword);
    this.loading.set(false);
    if (res.isSignedIn) {
      const { username } = this.form.value as { username: string; password: string; adminMode: boolean | null };
      if (username) {
        await this.cacheCustomerForEmail(username);
      }
      const isAdmin = this.lastAdminMode ?? false;
      this.setAdminMode(isAdmin);
      await this.navigateAfterSignIn(isAdmin);
    } else {
      this.error.set(res.error ?? 'Could not set password');
    }
  }
}
